import {
  isOntologyDisplayMode,
  isOntologyViewerPane,
  type OntologyDisplayMode,
  type OntologyViewerPane,
} from "./viewer/types";

/**
 * What the ontology browser shows. `version` and `termIri` name the item, so they belong in
 * the path of the Ontology section; `displayMode`, `pane` and `module` are view state, so
 * they live in the query string. Beside a document or a Thread, all of them live in the
 * query string.
 */
export interface OntologySelection {
  /** The commit of the model version; `null`: the latest. */
  readonly version: string | null;
  readonly termIri: string | null;
  readonly displayMode: OntologyDisplayMode;
  readonly pane: OntologyViewerPane;
  /** The item ID of the one ontology module shown; `null`: all modules. */
  readonly module: string | null;
}

type OntologyView = Pick<OntologySelection, "displayMode" | "pane" | "module">;

const DEFAULT_DISPLAY_MODE: OntologyDisplayMode = "structured";
const DEFAULT_PANE: OntologyViewerPane = "classes";

export const ONTOLOGY_QUERY_KEYS = {
  displayMode: "display",
  pane: "pane",
  module: "module",
  version: "ontologyVersion",
  termIri: "term",
} as const;

export function readOntologyView(query: URLSearchParams): OntologyView {
  const displayMode = query.get(ONTOLOGY_QUERY_KEYS.displayMode);
  const pane = query.get(ONTOLOGY_QUERY_KEYS.pane);
  return {
    displayMode: isOntologyDisplayMode(displayMode) ? displayMode : DEFAULT_DISPLAY_MODE,
    pane: isOntologyViewerPane(pane) ? pane : DEFAULT_PANE,
    module: query.get(ONTOLOGY_QUERY_KEYS.module),
  };
}

/** The query string with the view state of `selection`; other parameters stay. */
export function withOntologyView(query: URLSearchParams, selection: OntologySelection): URLSearchParams {
  const next = new URLSearchParams(query);
  setOrDelete(next, ONTOLOGY_QUERY_KEYS.displayMode, selection.displayMode, DEFAULT_DISPLAY_MODE);
  setOrDelete(next, ONTOLOGY_QUERY_KEYS.pane, selection.pane, DEFAULT_PANE);
  setOrDelete(next, ONTOLOGY_QUERY_KEYS.module, selection.module, null);
  return next;
}

/** The query string with the whole selection, for the ontology beside a document. */
export function withOntologySelection(query: URLSearchParams, selection: OntologySelection): URLSearchParams {
  const next = withOntologyView(query, selection);
  setOrDelete(next, ONTOLOGY_QUERY_KEYS.version, selection.version, null);
  setOrDelete(next, ONTOLOGY_QUERY_KEYS.termIri, selection.termIri, null);
  return next;
}

export function readOntologySelectionFromQuery(query: URLSearchParams): OntologySelection {
  return {
    ...readOntologyView(query),
    version: query.get(ONTOLOGY_QUERY_KEYS.version),
    termIri: query.get(ONTOLOGY_QUERY_KEYS.termIri),
  };
}

function setOrDelete(query: URLSearchParams, key: string, value: string | null, defaultValue: string | null) {
  if (value === null || value === defaultValue) {
    query.delete(key);
  } else {
    query.set(key, value);
  }
}
